import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { WorkOrderMtrInflationData, WorkOrderSeasonData } from 'gql/graphql';
import { Select } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import type { IRowDataChangeItems } from '../InflationDistributionModal';

interface InflationColumns {
    switchChecked: boolean;
    seasons: WorkOrderSeasonData[];
    addChangeRowInflation: () => void;
    actKC2MtrForInflationData: WorkOrderMtrInflationData[];
    changeFilterHandler: (params: { fieldName: string; checkedValues: string[] }) => void;
    yearInflationActKC2: number;
    setChangedRows: React.Dispatch<React.SetStateAction<IRowDataChangeItems[]>>;
}

export const useInflationDistributionColumns = ({
    switchChecked,
    seasons,
    actKC2MtrForInflationData,
    changeFilterHandler,
    addChangeRowInflation,
    yearInflationActKC2,
    setChangedRows,
}: InflationColumns) => {
    const filterItemsCode = actKC2MtrForInflationData?.map((item) => ({ label: item.code }));
    const filterItemsName = actKC2MtrForInflationData?.map((item) => ({ label: item.name }));
    const defaultSeasonsValue = seasons.find((obj) => obj.year === yearInflationActKC2)?.year || '';

    const [rowValues, setRowValues] = useState<Record<string, { id: number; label: number }>>({});

    const handleOnChange = useCallback(
        (rowId: string, { value: selectedValue }, defId) => {
            addChangeRowInflation();
            const isChangeRow = defId !== selectedValue.id;

            if (!selectedValue) {
                return;
            }

            setRowValues((prev) => ({
                ...prev,
                [rowId]: selectedValue,
            }));

            setChangedRows((prev) => ({
                ...prev,
                [rowId]: {
                    workOrderPriceId: rowId,
                    inflationSeasonId: selectedValue.id,
                    isChangeRow,
                },
            }));
        },
        [addChangeRowInflation, setChangedRows],
    );

    const filteredSeasons = useMemo(
        () => (yearInflationActKC2 ? seasons.filter((season) => season.year <= yearInflationActKC2) : []),
        [seasons, yearInflationActKC2],
    );

    const items = useMemo(
        () =>
            filteredSeasons.map((item) => ({
                id: item.id,
                label: item.year,
            })),
        [filteredSeasons],
    );

    const itemsWithIgnore = useMemo(() => [...items, { id: null, label: 'Не учитывать' }], [items]);

    const columns = useMemo<ColDef[]>(
        () => [
            {
                headerName: 'Шифр',
                field: 'code',
                minWidth: 184,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    changeFilterHandler,
                    filterItems: filterItemsCode,
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    childFieldAccessor: 'label',
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                cellRenderer: (params: { value: string }) => <span className="truncate">{params.value}</span>,
                filterParams: {
                    changeFilterHandler,
                    filterItems: filterItemsName,
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    childFieldAccessor: 'label',
                },
            },
            {
                headerName: 'Начисление инфляции',
                field: 'inf',
                minWidth: 230,
                spanHeaderHeight: true,
                cellRenderer: (dataRow: { data: WorkOrderMtrInflationData }) => {
                    const defaultItems = itemsWithIgnore.find(
                        (obj: { id: number }) => obj.id === dataRow?.data?.inflationSeasonId,
                    ) || {
                        id: null,
                        label: 'Не учитывать',
                    };

                    if (dataRow?.data?.parentId === null) {
                        return null;
                    }

                    if (switchChecked) {
                        return (
                            <Select
                                items={itemsWithIgnore}
                                value={rowValues[dataRow?.data?.id] || defaultItems}
                                onChange={(selectedValue) =>
                                    handleOnChange(dataRow?.data?.id, selectedValue, defaultItems?.id)
                                }
                                labelPosition="left"
                                size="s"
                            />
                        );
                    }

                    return <div>{defaultSeasonsValue}</div>;
                },
            },
        ],
        [
            changeFilterHandler,
            defaultSeasonsValue,
            filterItemsCode,
            filterItemsName,
            handleOnChange,
            itemsWithIgnore,
            rowValues,
            switchChecked,
        ],
    );

    return {
        columns,
    };
};
